import React, { useMemo } from 'react';

export interface PieChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface OverviewPieChartProps {
  data: PieChartDataPoint[];
  title: string;
  height?: number;
  showLegend?: boolean;
  className?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
];

export const OverviewPieChart: React.FC<OverviewPieChartProps> = ({
  data,
  title,
  height = 300,
  showLegend = true,
  className = '',
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { segments: [], total: 0 };
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return { segments: [], total: 0 };
    
    let currentAngle = -90; // Start from top
    const radius = 80;
    const centerX = 150;
    const centerY = 150;
    
    const segments = data.map((item, index) => {
      const percentage = (item.value / total) * 100;
      const angle = (item.value / total) * 360;
      
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      
      const x1 = centerX + radius * Math.cos(startAngleRad);
      const y1 = centerY + radius * Math.sin(startAngleRad);
      const x2 = centerX + radius * Math.cos(endAngleRad);
      const y2 = centerY + radius * Math.sin(endAngleRad);
      
      const largeArcFlag = angle > 180 ? 1 : 0;
      
      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ');
      
      const color = item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
      
      currentAngle += angle;
      
      return {
        ...item,
        percentage,
        pathData,
        color,
        startAngle,
        endAngle,
      };
    });
    
    return { segments, total };
  }, [data]);

  if (!data || data.length === 0 || chartData.total === 0) {
    return (
      <div className={`card p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <div className="flex flex-col lg:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <svg viewBox="0 0 300 300" className="w-64 h-64">
            {chartData.segments.map((segment, index) => (
              <g key={index}>
                <path
                  d={segment.pathData}
                  fill={segment.color}
                  stroke="white"
                  strokeWidth="2"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <title>
                    {segment.label}: {segment.value.toLocaleString()} ({segment.percentage.toFixed(1)}%)
                  </title>
                </path>
              </g>
            ))}
          </svg>
        </div>
        
        {showLegend && (
          <div className="flex-1 space-y-2">
            {chartData.segments.map((segment, index) => (
              <div key={index} className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: segment.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {segment.label}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                      {segment.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {segment.value.toLocaleString()} items
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
